// src/features/quests/useQuestEngine.js

import { useCallback, useEffect, useRef, useState } from "react";
import { getQuestState, advanceQuestProgress } from "../../api/QuestController";
import { getAllKeybinds } from "../../api/ClientsTagController";
import {
    getTargetRoomIdFromStep,
    shouldCelebrate,
    emitUnityCelebration,
    emitUnityChangeRoom,
    openLinkoutAndNotifyUnity,
    safeJsonParse,
} from "./questStepUtils";
import { normalizeQuestActionsFromSystemMessage } from "./fromSystemMessage";
import { joinQuestChannel, leaveQuestChannel, emitQuestAction } from "./questSocket"; // ✅ added emitQuestAction (non-breaking)

// Helper to get a valid roomId from localStorage (filters out "undefined", "null", empty strings)
const getValidLastRoomId = () => {
    const stored = localStorage.getItem("lastRoomId");
    if (!stored || stored === "undefined" || stored === "null" || !stored.trim()) {
        return null;
    }
    return stored;
};

export function useQuestEngine({ socket, userId, roomId, unityRoomIdForLinkout }) {
    const INITIAL = {
        loading: true,
        questId: null,
        questName: null,
        steps: [],
        currentStepIndex: 0,
        completed: false,
        error: null,
        isTeleporting: false,
    };

    const [questState, setQuestState] = useState(INITIAL);

    const questStateRef = useRef(questState);
    useEffect(() => {
        questStateRef.current = questState;
    }, [questState]);

    // Track current joined quest channel so we can leave when quest changes
    const joinedQuestIdRef = useRef(null);

    // Track teleport target room for auto-advancing after room change
    const teleportTargetRoomIdRef = useRef(null);

    // ✅ NEW: prevent infinite auto-advance loops on teleport-arrival
    const lastAutoTeleportAdvanceRef = useRef({ questId: null, stepIndex: null, roomId: null });

    // DANCE_KEYS tracking
    const allowedDanceKeysRef = useRef(new Set());
    const danceKeysRef = useRef(new Set());
    const danceCompletedRef = useRef(false);
    const keybindsLoadedRef = useRef(false);
    const keybindsLoadingRef = useRef(false);

    const applyQuestProgressUpdate = useCallback((updatedProgress) => {
        if (!updatedProgress) return;

        // Clear teleport target since we're updating progress
        teleportTargetRoomIdRef.current = null;

        setQuestState((prev) => {
            const stepsCount = prev.steps?.length || 0;
            const serverIndex = updatedProgress.currentStepIndex;

            // WORKAROUND: Backend may incorrectly return completed: true
            // Only trust completed: true if we've actually reached the end (index >= stepsCount)
            // Otherwise, ignore the completed flag and continue the quest
            const hasReachedEnd = stepsCount > 0 && serverIndex >= stepsCount;
            const isActuallyCompleted = hasReachedEnd || (!!updatedProgress.completed && serverIndex >= stepsCount - 1);

            // Clamp index to valid range - don't exceed last step index
            const maxValidIndex = Math.max(0, stepsCount - 1);
            const clampedIndex = stepsCount > 0 ? Math.min(serverIndex, maxValidIndex) : 0;

            console.log("[QUEST] applyQuestProgressUpdate:", {
                serverIndex,
                stepsCount,
                clampedIndex,
                serverCompleted: updatedProgress.completed,
                hasReachedEnd,
                isActuallyCompleted,
            });

            // If backend says completed but we haven't reached the end, log a warning
            if (updatedProgress.completed && !isActuallyCompleted) {
                console.warn(
                    "[QUEST] Backend returned completed:true but quest has remaining steps - ignoring completed flag",
                    {
                        serverIndex,
                        stepsCount,
                    }
                );
            }

            return {
                ...prev,
                currentStepIndex: clampedIndex,
                completed: isActuallyCompleted,
                error: null,
                isTeleporting: false,
            };
        });
    }, []);

    const reset = useCallback(() => {
        teleportTargetRoomIdRef.current = null;
        lastAutoTeleportAdvanceRef.current = { questId: null, stepIndex: null, roomId: null };
        setQuestState(INITIAL);
    }, []);

    // Helper to validate roomId is a proper UUID (not "undefined", "null", empty, etc.)
    const isValidRoomId = (id) => {
        if (!id || typeof id !== "string") return false;
        const trimmed = id.trim();
        if (!trimmed || trimmed === "undefined" || trimmed === "null") return false;
        // Basic UUID format check
        return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(trimmed);
    };

    const fetchQuestForRoom = useCallback(
        async (roomIdToLoad) => {
            if (!isValidRoomId(roomIdToLoad) || !userId) {
                console.warn("[useQuestEngine] fetchQuestForRoom skipped - invalid roomId or missing userId", {
                    roomIdToLoad,
                    userId,
                });
                return;
            }

            setQuestState((prev) => ({ ...prev, loading: true, error: null }));

            try {
                const res = await getQuestState(roomIdToLoad, userId);
                const data = res?.data || {};
                const quest = data.quest || null;
                const stepsFromServer = data.steps || [];
                const progressFromServer = data.progress || null;

                // Normalize step field names (backend may use snake_case or camelCase)
                const normalizedSteps = stepsFromServer.map((step) => ({
                    ...step,
                    // Ensure requiredEvent is available (backend might send required_event)
                    requiredEvent: step.requiredEvent || step.required_event || "",
                    // Ensure other fields are normalized
                    roomId: step.roomId || step.room_id || "",
                    stepOrder: step.stepOrder || step.step_order || 0,
                    promptText: step.promptText || step.prompt_text || "",
                    buttonLabel: step.buttonLabel || step.button_label || "Continue",
                    buttonMode: step.buttonMode || step.button_mode || "",
                }));

                console.log(
                    "[useQuestEngine] Normalized steps:",
                    normalizedSteps.map((s, i) => ({
                        index: i,
                        requiredEvent: s.requiredEvent,
                        buttonMode: s.buttonMode,
                    }))
                );

                console.log("[useQuestEngine] Quest data:", {
                    questId: quest?.id,
                    questName: quest?.name,
                    stepsCount: normalizedSteps.length,
                    progressFromServer,
                    serverCurrentStepIndex: progressFromServer?.currentStepIndex,
                });

                // Leave previous quest channel if we had one
                // Skip if teleporting or if current step is a teleport step (to avoid disconnecting mid-teleport)
                const currentStep = questStateRef.current.steps?.[questStateRef.current.currentStepIndex];
                const isOnTeleportStep = !!getTargetRoomIdFromStep(currentStep);
                const isTeleporting = questStateRef.current.isTeleporting;

                if (
                    joinedQuestIdRef.current &&
                    joinedQuestIdRef.current !== quest?.id &&
                    !isTeleporting &&
                    !isOnTeleportStep
                ) {
                    leaveQuestChannel(socket, { questId: joinedQuestIdRef.current, userId });
                    joinedQuestIdRef.current = null;
                }

                if (!quest) {
                    setQuestState({
                        loading: false,
                        questId: null,
                        questName: null,
                        steps: [],
                        currentStepIndex: 0,
                        completed: false,
                        error: null,
                        isTeleporting: false,
                    });
                    return;
                }

                const serverIndex =
                    typeof progressFromServer?.currentStepIndex === "number" ? progressFromServer.currentStepIndex : 0;

                // CRITICAL FIX: Ensure currentStepIndex doesn't exceed the number of steps
                // WORKAROUND: Backend may incorrectly return completed: true at step 0
                // Only trust completed if we've actually reached/passed the last step
                const maxValidIndex = Math.max(0, normalizedSteps.length - 1);
                const clampedServerIndex = Math.min(serverIndex, maxValidIndex);
                const hasReachedEnd = normalizedSteps.length > 0 && serverIndex >= normalizedSteps.length;
                const isActuallyCompleted =
                    hasReachedEnd || (!!progressFromServer?.completed && serverIndex >= normalizedSteps.length - 1);

                if (progressFromServer?.completed && !isActuallyCompleted) {
                    console.warn(
                        "[useQuestEngine] Backend returned completed:true but quest has remaining steps - ignoring!",
                        {
                            serverIndex,
                            stepsCount: normalizedSteps.length,
                            serverCompleted: progressFromServer?.completed,
                        }
                    );
                }

                if (serverIndex >= normalizedSteps.length && !progressFromServer?.completed) {
                    console.warn("[useQuestEngine] Server currentStepIndex exceeds steps count!", {
                        serverIndex,
                        stepsCount: normalizedSteps.length,
                        clampedTo: clampedServerIndex,
                        markingAsCompleted: true,
                    });
                }

                setQuestState((prev) => {
                    const sameQuest = prev.questId === quest.id;
                    const prevIndex = sameQuest ? prev.currentStepIndex : 0;
                    // Use clamped index to prevent out-of-bounds
                    const effectiveIndex = isActuallyCompleted
                        ? clampedServerIndex
                        : Math.min(Math.max(clampedServerIndex, prevIndex), maxValidIndex);

                    console.log("[useQuestEngine] Setting quest state:", {
                        effectiveIndex,
                        isActuallyCompleted,
                        stepsCount: normalizedSteps.length,
                    });

                    return {
                        ...prev,
                        loading: false,
                        questId: quest.id,
                        questName: quest.name || null,
                        steps: normalizedSteps,
                        currentStepIndex: effectiveIndex,
                        completed: isActuallyCompleted,
                        error: null,
                        isTeleporting: prev.isTeleporting, // preserve current teleport flag
                    };
                });

                // Join quest channel (once per quest)
                // Skip if teleporting (we're already joined to the quest channel)
                if (socket && quest.id && joinedQuestIdRef.current !== quest.id && !isTeleporting) {
                    joinQuestChannel(socket, { questId: quest.id, userId });
                    joinedQuestIdRef.current = quest.id;
                }
            } catch (err) {
                setQuestState((prev) => ({ ...prev, loading: false, error: "Failed to load quest" }));
            }
        },
        [socket, userId]
    );

    useEffect(() => {
        return () => {
            if (joinedQuestIdRef.current) {
                leaveQuestChannel(socket, { questId: joinedQuestIdRef.current, userId });
                joinedQuestIdRef.current = null;
            }
        };
    }, [socket, userId]);

    useEffect(() => {
        const step = questState.steps?.[questState.currentStepIndex];
        const required = String(step?.requiredEvent || "").trim();
        if (required.startsWith("DANCE_KEYS")) {
            danceKeysRef.current = new Set();
            danceCompletedRef.current = false;
        }
    }, [questState.currentStepIndex, questState.steps]);

    // Helper: classify system vs custom required events
    const isNotASystemMessage = (action) => {
        const DEFAULT_REQUIRED_EVENTS = [
            "OPEN_GUESTBOOK",
            "EDIT_RPM_AVATAR",
            "OPEN_SMALL_IFRAME",
            "QUIZ_COMPLETE",
            "NEW_ROOM_LOADED",
            "OPEN_LINKOUT",
            "DYNAMIC_LINKOUT",
            "NPC_OPEN",
            "BUTTON_ONLY",
        ];
        if (DEFAULT_REQUIRED_EVENTS.includes(action)) {
            return false;
        }
        return true;
    };

    // ✅ Send STARTED:<custom_event> when entering a custom required event step
    useEffect(() => {
        const step = questState.steps?.[questState.currentStepIndex];
        const required = String(step?.requiredEvent || "").trim();
        if (/^[A-Z0-9_]+$/.test(required)) {
            if (socket && userId && questState.questId && isNotASystemMessage(required)) {
                emitQuestAction(socket, {
                    questId: questState.questId,
                    userId,
                    roomId: roomId || getValidLastRoomId() || null,
                    actionType: `STARTED:${required}`,
                    payload: {},
                });
            }
        }
    }, [questState.currentStepIndex, questState.steps, questState.questId, socket, userId, roomId]);

    const ensureKeybinds = useCallback(async () => {
        if (keybindsLoadedRef.current || keybindsLoadingRef.current) return;

        keybindsLoadingRef.current = true;
        try {
            const result = await getAllKeybinds();
            const raw = result?.data?.keyOptions;
            const list = Array.isArray(raw) ? raw : [];

            allowedDanceKeysRef.current = new Set(
                list
                    .map((k) =>
                        String(k || "")
                            .trim()
                            .toUpperCase()
                    )
                    .filter(Boolean)
            );

            keybindsLoadedRef.current = true;
        } catch (e) {
            keybindsLoadedRef.current = false;
        } finally {
            keybindsLoadingRef.current = false;
        }
    }, []);

    // Map action -> API eventType
    const toApiEventType = useCallback((actionType, actionRoomId) => {
        if (!actionType) return null;

        if (actionType === "NEW_ROOM_LOADING" && actionRoomId) {
            return `NEW_ROOM_LOADING:${actionRoomId}`;
        }

        return actionType;
    }, []);

    const doAdvance = useCallback(
        async ({ eventType, roomIdOverride }) => {
            const qs = questStateRef.current;

            console.log("[QUEST] doAdvance called:", {
                questId: qs.questId,
                completed: qs.completed,
                currentStepIndex: qs.currentStepIndex,
                stepsCount: qs.steps?.length,
                eventType,
                roomIdOverride,
            });

            if (!qs.questId || qs.completed) {
                console.log("[QUEST] doAdvance early return - no questId or already completed");
                return;
            }

            const lastRoomId = getValidLastRoomId();
            const effectiveRoomId =
                (roomIdOverride && String(roomIdOverride).trim()) ||
                lastRoomId ||
                (roomId && String(roomId).trim()) ||
                null;

            if (!effectiveRoomId || !userId) {
                console.debug("[QUEST] doAdvance deferred - waiting for roomId/userId", {
                    roomIdOverride,
                    roomId,
                    lastRoomId,
                    userId,
                    eventType,
                });
                return;
            }

            const currentStep = qs.steps?.[qs.currentStepIndex];

            console.log("[QUEST] Current step before advance:", {
                stepIndex: qs.currentStepIndex,
                requiredEvent: currentStep?.requiredEvent,
                eventType,
                effectiveRoomId,
            });

            if (shouldCelebrate(currentStep)) {
                emitUnityCelebration(socket, userId);
            }

            try {
                const res = await advanceQuestProgress({
                    roomId: effectiveRoomId,
                    userId,
                    eventType: eventType || null,
                });

                console.log("[QUEST] advanceQuestProgress response:", res?.data);

                if (!res?.data?.progress) {
                    console.warn("[QUEST] advanceQuestProgress returned no progress", {
                        status: res?.status,
                        data: res?.data,
                        effectiveRoomId,
                        eventType,
                    });
                    return;
                }

                applyQuestProgressUpdate(res.data.progress);
            } catch (err) {
                console.error("[QUEST] Failed to advance quest", err);
                setQuestState((prev) => ({ ...prev, error: "Failed to advance quest" }));
            }
        },
        [applyQuestProgressUpdate, roomId, socket, userId]
    );

    // ✅ IMPORTANT: auto-fetch whenever roomId changes
    // Also handle teleport completion - advance quest when room changes to target (same-session)
    useEffect(() => {
        if (!roomId || !userId) return;

        const targetRoomId = teleportTargetRoomIdRef.current;
        const isTeleporting = questStateRef.current.isTeleporting;

        // If we were teleporting and arrived at the target room, advance the quest
        if (isTeleporting && targetRoomId && roomId === targetRoomId) {
            console.log("[QUEST] Teleport completed (same session), advancing quest", { roomId, targetRoomId });
            teleportTargetRoomIdRef.current = null;
            doAdvance({
                eventType: `NEW_ROOM_LOADING:${roomId}`,
                roomIdOverride: roomId,
            });
            return;
        }

        // Normal case: fetch quest for the new room (only if not mid-teleport)
        if (!isTeleporting) {
            fetchQuestForRoom(roomId);
        }
    }, [roomId, userId, fetchQuestForRoom, doAdvance]);

    // ✅ NEW FIX #2: if modal/hook remounts on arrival, still auto-advance teleport step
    // (doesn't rely on isTeleporting/ref)
    useEffect(() => {
        const qs = questStateRef.current;
        if (!qs?.questId || qs.completed) return;

        const step = qs.steps?.[qs.currentStepIndex];
        if (!step) return;

        const targetRoomId = getTargetRoomIdFromStep(step);
        if (!targetRoomId) return;

        if (!roomId || roomId !== targetRoomId) return;

        const last = lastAutoTeleportAdvanceRef.current;
        const sameAsLast =
            last.questId === qs.questId && last.stepIndex === qs.currentStepIndex && last.roomId === roomId;

        if (sameAsLast) return;

        lastAutoTeleportAdvanceRef.current = { questId: qs.questId, stepIndex: qs.currentStepIndex, roomId };

        console.log("[QUEST] Already in teleport target room (after reload), auto-advancing", {
            questId: qs.questId,
            stepIndex: qs.currentStepIndex,
            roomId,
            targetRoomId,
        });

        doAdvance({
            eventType: `NEW_ROOM_LOADING:${roomId}`,
            roomIdOverride: roomId,
        });
    }, [roomId, questState.currentStepIndex, questState.steps, questState.questId, questState.completed, doAdvance]);

    // --- AUTO-PROGRESS ON ERROR FOR CUSTOM EVENTS ---
    useEffect(() => {
        const step = questState.steps?.[questState.currentStepIndex];
        const required = String(step?.requiredEvent || "").trim();
        if (questState.error && isNotASystemMessage(required)) {
            setQuestState((prev) => ({ ...prev, error: null }));
            Promise.resolve().then(() => {
                doAdvance({
                    eventType: required, // ✅ send REQUIRED (backend strict), not COMPLETED:REQUIRED
                    roomIdOverride: roomId || getValidLastRoomId(),
                });
            });
        }
    }, [questState.currentStepIndex, questState.steps, questState.error, doAdvance, roomId]);

    // Helper to match custom required event
    function matchesCustomRequiredEvent(required, actionType) {
        const req = String(required || "").trim();
        const act = String(actionType || "").trim();

        if (!req || !act) return false;

        // Progress if actionType is exactly required
        if (act === req) return true;

        // Progress if actionType is exactly COMPLETED:required
        if (act === `COMPLETED:${req}`) return true;

        // Optional: if it contains COMPLETED and the required token, treat it as match
        // (supports cases like "SOMETHING_COMPLETED:REQ" or "COMPLETED:REQ:EXTRA")
        if (act.includes("COMPLETED") && act.includes(req)) return true;

        return false;
    }

    // ✅ FIX #1: normalize custom COMPLETED:* events BEFORE sending to backend
    function normalizeCustomEventForApi(required, actionType) {
        const req = String(required || "").trim();
        const act = String(actionType || "").trim();

        if (!req || !act) return act;

        if (act === req) return req;
        if (act === `COMPLETED:${req}`) return req;
        if (act.includes("COMPLETED") && act.includes(req)) return req;

        return act;
    }

    const actionMatchesCurrentStep = useCallback((actionType, payload, actionRoomId) => {
        const qs = questStateRef.current;
        const step = qs.steps?.[qs.currentStepIndex];
        if (!step) return false;

        const required = String(step.requiredEvent || "").trim();

        // Don’t auto-advance on button steps
        if (!required || required === "BUTTON_ONLY") return false;

        // Step expects NEW_ROOM_LOADING:<roomId>
        if (required.startsWith("NEW_ROOM_LOADING:")) {
            const requiredRoomId = required.split(":")[1];
            return actionType === "NEW_ROOM_LOADING" && !!requiredRoomId && requiredRoomId === actionRoomId;
        }

        if (required.startsWith("OPEN_LINKOUT")) return actionType === "OPEN_LINKOUT";

        if (required === "DANCE_KEYS") return actionType === "DANCE_KEYS_COMPLETE";

        // Custom required event
        if (isNotASystemMessage(required)) {
            return matchesCustomRequiredEvent(required, actionType);
        }

        return required === actionType;
    }, []);

    const dispatchAction = useCallback(
        async ({ actionType, payload, roomId: actionRoomId }) => {
            const qs = questStateRef.current;
            if (!qs.questId || qs.completed) return;

            const effectiveRoomId = actionRoomId || roomId || getValidLastRoomId();
            if (!effectiveRoomId) {
                console.debug("[QUEST] dispatchAction skipped - no roomId available yet");
                return;
            }

            const step = qs.steps?.[qs.currentStepIndex];
            const required = String(step?.requiredEvent || "").trim();

            if (actionType === "AVATAR_ACTION" && (required === "DANCE_KEYS" || required === "DANCE_KEYS_COMPLETE")) {
                await ensureKeybinds();
                const rawKey = payload?.key;
                const normalizedKey = String(rawKey || "")
                    .trim()
                    .toUpperCase();
                if (!normalizedKey) return;
                const allowedSet = allowedDanceKeysRef.current;
                if (allowedSet?.size > 0 && !allowedSet.has(normalizedKey)) return;
                danceKeysRef.current.add(normalizedKey);
                if (!danceCompletedRef.current && danceKeysRef.current.size >= 5) {
                    danceCompletedRef.current = true;
                    await doAdvance({
                        eventType: "DANCE_KEYS_COMPLETE",
                        roomIdOverride: actionRoomId || roomId || getValidLastRoomId(),
                    });
                }
                return;
            }

            // ✅ Custom required events:
            // - accept "REQUIRED" or "COMPLETED:REQUIRED"
            // - but send ONLY "REQUIRED" to backend (backend strict equality)
            if (isNotASystemMessage(required)) {
                if (actionType === `ERROR:${required}`) {
                    setQuestState((prev) => ({ ...prev, error: `Step failed: ${required}` }));
                    return;
                }

                if (matchesCustomRequiredEvent(required, actionType)) {
                    const apiEventType = normalizeCustomEventForApi(required, actionType);
                    await doAdvance({ eventType: apiEventType, roomIdOverride: actionRoomId });
                    return;
                }

                return;
            }

            if (actionMatchesCurrentStep(actionType, payload, actionRoomId)) {
                const apiEventType = toApiEventType(actionType, actionRoomId);
                await doAdvance({ eventType: apiEventType, roomIdOverride: actionRoomId });
            }
        },
        [actionMatchesCurrentStep, doAdvance, ensureKeybinds, toApiEventType]
    );

    const ingestSystemMessage = useCallback(
        async (response) => {
            if (!userId) return;

            const actions = normalizeQuestActionsFromSystemMessage(response, userId);

            await actions.reduce((p, a) => {
                return p.then(() =>
                    dispatchAction({
                        actionType: a.actionType,
                        payload: a.payload || {},
                        roomId: a.roomId || null,
                    })
                );
            }, Promise.resolve());
        },
        [dispatchAction, userId]
    );

    const ingestQuestMessage = useCallback(
        async (payload) => {
            if (!userId) return;

            const msg = safeJsonParse(payload);
            if (!msg || typeof msg !== "object") return;

            // Only handle quest channel envelope
            if (msg.type !== "QUEST_ACTION") return;

            // Must match current quest (if questId is provided)
            const qs = questStateRef.current;
            if (!qs?.questId || qs.completed) return;
            if (msg.questId && msg.questId !== qs.questId) return;

            const actor = msg.actorUserId;
            const targets = msg?.targets?.userIds;
            const isForMe = actor === userId || (Array.isArray(targets) && targets.includes(userId));
            if (!isForMe) return;

            const actionType = String(msg?.action?.actionType || "").trim();
            if (!actionType) return;

            const actionRoomId = msg.roomId || null;

            await dispatchAction({
                actionType,
                payload: msg?.action?.payload || {},
                roomId: actionRoomId,
            });
        },
        [dispatchAction, userId]
    );

    const sendQuestAction = useCallback(
        ({ actionType, payload, roomId: actionRoomId }) => {
            const qs = questStateRef.current;
            if (!socket || !userId || !qs?.questId || !actionType) return;

            emitQuestAction(socket, {
                questId: qs.questId,
                userId,
                roomId: actionRoomId || roomId || getValidLastRoomId() || null,
                actionType,
                payload: payload || {},
            });
        },
        [socket, userId, roomId]
    );

    const handleButtonClick = useCallback(
        async ({ setIsLoading, openSmallIframe }) => {
            const qs = questStateRef.current;
            if (!qs.questId || qs.completed) return;

            const step = qs.steps?.[qs.currentStepIndex];
            if (!step) return;

            // Room change step: normally emit only, advance happens when NEW_ROOM_LOADING:<roomId> comes back
            const targetRoomId = getTargetRoomIdFromStep(step);
            if (targetRoomId) {
                const currentUnityRoomId = getValidLastRoomId() || roomId;
                if (currentUnityRoomId && currentUnityRoomId === targetRoomId) {
                    // Already in target room, just advance
                    if (typeof setIsLoading === "function") setIsLoading(false);
                    await doAdvance({
                        eventType: `NEW_ROOM_LOADING:${targetRoomId}`,
                        roomIdOverride: targetRoomId,
                    });
                    return;
                }

                // Store target room and set teleporting state
                teleportTargetRoomIdRef.current = targetRoomId;
                setQuestState((prev) => ({ ...prev, error: null, isTeleporting: true }));

                if (typeof setIsLoading === "function") setIsLoading(true);

                emitUnityChangeRoom(socket, userId, targetRoomId);
                return;
            }

            // Linkout step - open the link and advance the quest
            const required = String(step.requiredEvent || "").trim();
            if (required.startsWith("OPEN_LINKOUT")) {
                const meta = safeJsonParse(step.metadata);
                const url = meta?.url;
                openLinkoutAndNotifyUnity(socket, { url, roomId: unityRoomIdForLinkout || roomId, userId });

                const effectiveRoomId = roomId || getValidLastRoomId();
                if (effectiveRoomId) {
                    await doAdvance({ eventType: "OPEN_LINKOUT", roomIdOverride: effectiveRoomId });
                }
                return;
            }

            // Small iframe step - open the small iframe modal and advance the quest
            if (required === "OPEN_SMALL_IFRAME") {
                const meta = safeJsonParse(step.metadata);
                const url = meta?.url;
                if (typeof openSmallIframe === "function" && url) {
                    openSmallIframe(url);
                }
                const effectiveRoomId = roomId || getValidLastRoomId();
                if (effectiveRoomId) {
                    await doAdvance({ eventType: "OPEN_SMALL_IFRAME", roomIdOverride: effectiveRoomId });
                }
                return;
            }

            // Don’t allow skipping event-required steps
            if (required && required !== "BUTTON_ONLY") {
                setQuestState((prev) => ({ ...prev, error: "Complete the current objective in the room first." }));
                return;
            }

            const effectiveRoomId = roomId || getValidLastRoomId();
            if (!effectiveRoomId) {
                console.debug("[QUEST] handleButtonClick deferred - no roomId available yet");
                return;
            }

            // Send null for BUTTON_ONLY steps - backend should treat null/empty as "any button click"
            await doAdvance({ eventType: null, roomIdOverride: effectiveRoomId });
        },
        [doAdvance, roomId, socket, unityRoomIdForLinkout, userId]
    );

    return {
        questState,
        setQuestState,
        fetchQuestForRoom,
        ingestSystemMessage,
        ingestQuestMessage,
        dispatchAction,
        handleButtonClick,
        reset,
        sendQuestAction,
    };
}
